import { useState, useCallback, useEffect } from 'react';
import type { FieldPlacement, ClaimFieldDefinition } from '@/types/template-fields';
import { createDefaultPlacement } from '@/types/template-fields';
import { FieldPalette } from './FieldPalette';
import { PdfTemplateViewer } from './PdfTemplateViewer';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

export interface TemplateFieldEditorToolbarState {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  canUndo: boolean;
  previewMode: boolean;
  onUndo: () => void;
  onTogglePreview: () => void;
  onSave: () => void;
  onOpenPalette: () => void;
  onOpenProperties: () => void;
  canOpenProperties: boolean;
}

interface TemplateFieldEditorProps {
  pdfUrl: string;
  initialPlacements: FieldPlacement[];
  onSave: (placements: FieldPlacement[]) => Promise<void>;
  onToolbarStateChange: (state: TemplateFieldEditorToolbarState) => void;
}

export function TemplateFieldEditor({
  pdfUrl,
  initialPlacements,
  onSave,
  onToolbarStateChange,
}: TemplateFieldEditorProps) {
  const [placements, setPlacements] = useState<FieldPlacement[]>(initialPlacements);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [historyState, setHistoryState] = useState<{
    history: FieldPlacement[][];
    index: number;
  }>({
    history: [initialPlacements],
    index: 0,
  });

  // Sync state when initialPlacements arrives asynchronously
  useEffect(() => {
    setPlacements(initialPlacements);
    setHistoryState({
      history: [initialPlacements],
      index: 0,
    });
    setSelectedFieldId(null);
    setHasUnsavedChanges(false);
  }, [initialPlacements]);

  const selectedPlacement =
    placements.find((p) => p.id === selectedFieldId) ?? null;

  const pushToHistory = useCallback(
    (newPlacements: FieldPlacement[]) => {
      setHistoryState((prev) => {
        const newHistory = prev.history.slice(0, prev.index + 1);
        newHistory.push(newPlacements);
        return {
          history: newHistory,
          index: prev.index + 1,
        };
      });
    },
    [],
  );

  const handlePlacementsChange = useCallback(
    (newPlacements: FieldPlacement[]) => {
      setPlacements(newPlacements);
      setHasUnsavedChanges(true);
      // No history push - live updates only
    },
    [],
  );

  const handlePlacementsChangeComplete = useCallback(
    (newPlacements: FieldPlacement[]) => {
      setPlacements(newPlacements);
      setHasUnsavedChanges(true);
      pushToHistory(newPlacements);
    },
    [pushToHistory],
  );

  const handleUndo = useCallback(() => {
    setHistoryState((prev) => {
      if (prev.index > 0) {
        const newIndex = prev.index - 1;
        setPlacements(prev.history[newIndex]);
        setHasUnsavedChanges(true);
        return {
          history: prev.history,
          index: newIndex,
        };
      }
      return prev;
    });
  }, []);

  const handleFieldUpdate = useCallback(
    (updated: FieldPlacement) => {
      const newPlacements = placements.map((p) =>
        p.id === updated.id ? updated : p,
      );
      // Property updates are discrete, should push to history
      handlePlacementsChangeComplete(newPlacements);
    },
    [placements, handlePlacementsChangeComplete],
  );

  const handleFieldDelete = useCallback(
    (id: string) => {
      // Delete is discrete, should push to history
      handlePlacementsChangeComplete(placements.filter((p) => p.id !== id));
      if (selectedFieldId === id) setSelectedFieldId(null);
    },
    [placements, handlePlacementsChangeComplete, selectedFieldId],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(placements);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save field placements:', error);
    } finally {
      setIsSaving(false);
    }
  }, [placements, onSave]);

  const handleTogglePreview = useCallback(() => {
    setPreviewMode((current) => !current);
  }, []);

  const handleOpenPalette = useCallback(() => {
    setIsPaletteOpen(true);
  }, []);

  const handleOpenProperties = useCallback(() => {
    setIsPropertiesOpen(true);
  }, []);

  useEffect(() => {
    onToolbarStateChange({
      hasUnsavedChanges,
      isSaving,
      canUndo: historyState.index > 0,
      previewMode,
      onUndo: handleUndo,
      onTogglePreview: handleTogglePreview,
      onSave: handleSave,
      onOpenPalette: handleOpenPalette,
      onOpenProperties: handleOpenProperties,
      canOpenProperties: Boolean(selectedPlacement),
    });
  }, [
    hasUnsavedChanges,
    isSaving,
    historyState.index,
    previewMode,
    handleUndo,
    handleTogglePreview,
    handleSave,
    handleOpenPalette,
    handleOpenProperties,
    selectedPlacement,
    onToolbarStateChange,
  ]);

  const handleAddCustomField = useCallback(
    (fieldDef: ClaimFieldDefinition) => {
      const initialPlacement = createDefaultPlacement(fieldDef, currentPage - 1, 0, 0);
      const placement = {
        ...initialPlacement,
        x: (100 - initialPlacement.width) / 2,
        y: (100 - initialPlacement.height) / 2,
      };
      // Adding a field is discrete, should push to history
      handlePlacementsChangeComplete([...placements, placement]);
      setSelectedFieldId(placement.id);
    },
    [placements, handlePlacementsChangeComplete, currentPage],
  );

  const handlePickFieldFromPalette = useCallback(
    (fieldDef: ClaimFieldDefinition) => {
      handleAddCustomField(fieldDef);
      setIsPaletteOpen(false);
    },
    [handleAddCustomField],
  );

  // Keyboard handler: Delete/Backspace removes selected field
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedFieldId &&
        !previewMode
      ) {
        if (
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return;
        }
        handleFieldDelete(selectedFieldId);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFieldId, handleFieldDelete, previewMode]);

  return (
    <div className="flex flex-col h-full">
      {!previewMode && (
        <>
          <Sheet open={isPaletteOpen} onOpenChange={setIsPaletteOpen}>
            <SheetContent side="left" className="w-[92vw] max-w-[360px] overflow-hidden p-0 pt-12 md:hidden">
              <SheetTitle className="sr-only">Field Palette</SheetTitle>
              <FieldPalette
                onAddCustomField={handleAddCustomField}
                onPickField={handlePickFieldFromPalette}
                enableTapToAdd
                className="h-full w-full max-w-none border-r-0"
              />
            </SheetContent>
          </Sheet>

          <Sheet open={isPropertiesOpen} onOpenChange={setIsPropertiesOpen}>
            <SheetContent side="right" className="w-[92vw] max-w-[360px] overflow-hidden p-0 pt-12 md:hidden">
              <SheetTitle className="sr-only">Field Properties</SheetTitle>
              <FieldPropertiesPanel
                placement={selectedPlacement}
                onUpdate={handleFieldUpdate}
                onDelete={handleFieldDelete}
                className="h-full w-full max-w-none border-l-0"
              />
            </SheetContent>
          </Sheet>
        </>
      )}

      {/* Editor body */}
      <div className="flex flex-1 overflow-hidden">
        {!previewMode && (
          <FieldPalette
            onAddCustomField={handleAddCustomField}
            className="hidden md:flex"
          />
        )}
        <PdfTemplateViewer
          pdfUrl={pdfUrl}
          placements={placements}
          onPlacementsChange={handlePlacementsChange}
          onPlacementsChangeComplete={handlePlacementsChangeComplete}
          selectedFieldId={selectedFieldId}
          onSelectField={setSelectedFieldId}
          previewMode={previewMode}
          currentPage={currentPage}
          onCurrentPageChange={setCurrentPage}
        />
        {!previewMode && (
          <FieldPropertiesPanel
            placement={selectedPlacement}
            onUpdate={handleFieldUpdate}
            onDelete={handleFieldDelete}
            className="hidden md:block"
          />
        )}
      </div>
    </div>
  );
}
